/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file index.js
 * @description Patient Services
 */

// Core
import endpoints from '@nephro/config/endpoints';
import { externalConnector } from '@nephro/core/connector';

// Endpoints
const { patient: patientEndpoints, authentication: authEndpoints } = endpoints;

export class PatientService {
  constructor(http) {
    this.http = http;
  }

  /**
   * Create patient
   * @param { object } data - Patient data.
   * @return { promise } - The patiend promise object.
   */
  createPatient = async (data) => {
    const body = { ...data };
    const url = patientEndpoints.base;
    return this.http.post(url, body);
  };

  /**
   * Create patient in Okta
   * @param { object } data - Patient data.
   * @return { promise } - The patiend promise object.
   */
  createPatientOkta = async (data) => {
    const body = { ...data };
    const url = authEndpoints.create;
    return this.http.post(url, body);
  };

  /**
   * Get patients list without pagination
   * @return { Object } - Get patients list.
   */
  getPatients = async () => {
    const url = patientEndpoints.getAllPatients;

    return this.http.get(url);
  };

  /**
   * Get patient info by id
   * @param { string } id - Patient id.
   * @return { Object } - Get patients list.
   */
  getPatientById = async (id) => {
    const url = `${patientEndpoints.base}/${id}`;
    return this.http.get(url);
  };

  /**
   * Get patient last appointment by id versia
   * @param { string } id - Patient id versia.
   * @return { Object } - Get patients list.
   */
  getPatientLastAppointment = async (id) => {
    const url = `${patientEndpoints.patientLastAppointment}?versiaPatientId=${id}`;
    return this.http.get(url);
  };

  /**
   * Get patient extra info by id
   * @param { string } id - Patient id.
   * @return { Object } - Get patient info.
   */
  getPatientExtraDataById = async (id) => {
    const url = `${patientEndpoints.extraDataById}/${id}`;
    return this.http.get(url);
  };

  /**
   * Get patient info by validateById
   * @param { string } document - Patient document.
   * @param { string } documentType - Patient document type.
   * @return { Object } - Get patients list.
   */
  getPatientByDocument = async (document, documentType) => {
    const url = `${patientEndpoints.validateById}/${document}/${documentType}`;
    return this.http.get(url);
  };

  /**
   * Get patient info by validateById
   * @param { string } document - Patient document.
   * @param { string } documentType - Patient document type.
   * @return { Object } - Get patients list.
   */
  getEmailPatientByDocument = async (document, documentType) => {
    const url = `${patientEndpoints.validateEmailById}/${document}/${documentType}`;
    return this.http.get(url);
  };

  /**
   * Delete patient by id
   * @param { string } id - Patient id.
   * @return { Object } - Get patients list.
   */
  deletePatientById = async (id) => {
    const url = `${patientEndpoints.base}/${id}`;
    return this.http.delete(url);
  };

  /**
   * Update patient
   * @param { object } data - Patient data.
   * @return { promise } - The patient promise object.
   */
  updatePatient = async (data, id) => {
    const body = { ...data, id };
    const url = `${patientEndpoints.base}/${id}`;
    return this.http.update(url, body, {}, 'put');
  };

  /**
   * Update patient status
   * @param { object } data - Patient data.
   * @param { object } id - Patient data.
   * @return { promise } - The patient promise object.
   */
  updatePatientStatus = async (id, statusName) => {
    const url = `${patientEndpoints.updatePatientStatus}/${id}/${statusName}`;
    return this.http.post(url);
  };

  /**
   * Get identification type list with pagination
   * @param { string } paged - Activate pagination.
   * @param { string } limit - Items per page.
   * @param { string } page - Current page.
   * @return { Object } - Get identification type list.
   */
  getIdentificationTypes = async (paged, limit, page) => {
    const queryParam = `?limit=${limit}&page=${page}&paged=${paged}`;
    let url = patientEndpoints.typeId;
    if (paged) {
      url = `${url}${queryParam}`;
    }
    return this.http.get(url);
  };

  /**
   * Get patient status list.
   * @param { string } url - Url patient status service
   * @return { Object } - Get patient status list.
   */
  getPatientStatusList = async () => {
    const url = patientEndpoints.statusList;
    return this.http.get(url);
  };

  /**
   * Get patient indicator info
   * @param { string } patientId - Patient id.
   * @return { Object } - Get patients indicator.
   */
  getPatientIndicators = async (patientId) => {
    const url = `${patientEndpoints.patientIndicators}/${patientId}`;
    return this.http.get(url);
  };

  /**
   * Get patient indicator info
   * @param { string } patientId - Patient id.
   * @return { Object } - Get patients events.
   */
  getEventTypesByPatientId = async (patientId) => {
    /* return { data: patientIndicatorsMock }; */
    const url = `${patientEndpoints.eventsByPatientId}/${patientId}`;
    return this.http.get(url);
  };

  /**
   * Get patients list with pagination
   * @param { string } paged - Activate pagination.
   * @param { string } limit - Items per page.
   * @param { string } page - Current page.
   * @param { string } filterWord - word to filter.
   * @return { Object } - Get patients list.
   */
  getFilterPatients = async (paged, limit, page, filterWord, clinicId) => {
    let url = patientEndpoints.filter;
    if (paged) {
      const queryParam = `/${filterWord}?limit=${limit}&page=${page}&paged=${paged}&clinicId=${clinicId}`;
      url = `${url}${queryParam}`;
    }
    return this.http.post(url);
  };

  /**
   * Get patient indicator info
   * @param { string } document - Patient document.
   * @param { string } documentTypeId - Patient documentTypeId from Versia.
   * @param { string } result - Patient PTH result.
   * @return { Object } - Get PTH patient.
   */
  getPthPatient = async (document, documentTypeId, result) => {
    const url = patientEndpoints.PTHPatient;
    const queryParam = `?documento=${document}&tipoDocumento=${documentTypeId}&resultado=${result}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get upcoming video calls info
   * @param { string } clinicId - clinicId.
   * @param { string } limit - limit
   * @param { string } offset - offset
   * @return { Object } - Get upcoming video calls
   */
  getUpcomingVideoCalls = async (clinicId, limit, offset) => {
    const url = patientEndpoints.upcomingVideoCalls;
    const queryParam = `?idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get upcoming video calls info
   * @param { Array } - patients array.
   * @return { Object } - Get upcoming video calls
   */
  sendMassInvitation = async (patientList) => {
    const url = patientEndpoints.sendMassInvitation;
    return this.http.post(`${url}`, patientList);
  };

  /**
   * Get upcoming video calls info
   * @param { Array } - patients array.
   * @return { Object } - Get upcoming video calls
   */
  listPatientMProcessing = async () => {
    const url = patientEndpoints.patientMProcessing;
    return this.http.get(`${url}`);
  };

  /**
   * Get upcoming video calls info
   * @param { Array } - patients array.
   * @return { Object } - Get upcoming video calls
   */
  patientMProcessDetail = async (id) => {
    const url = patientEndpoints.patientMProcessDetail;
    return this.http.get(`${url}/${id}`);
  };
}

export default new PatientService(externalConnector);
